import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Dialog, IconButton, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useNotification } from "../notificationProvider";
import { AnexoNovo, formatarTamanho, tipoDoMime, TipoAnexo } from "../../pages/suporte/types";
import { styless } from "../../pages/suporte/styles";

export const MAX_ARQUIVO_MB = 10;
const MAX_ARQUIVO_BYTES = MAX_ARQUIVO_MB * 1024 * 1024;
const MAX_TOTAL_BYTES = 9 * 1024 * 1024;

const MIMES_PERMITIDOS: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "text/csv": "csv",
  "text/plain": "txt",
  "application/zip": "zip",
};

const MIME_POR_EXTENSAO = Object.entries(MIMES_PERMITIDOS).reduce(
  (acc, [mime, ext]) => {
    if (!acc[ext]) acc[ext] = mime;
    return acc;
  },
  { jpeg: "image/jpeg" } as Record<string, string>,
);

export const ACCEPT_ANEXOS = Object.keys(MIMES_PERMITIDOS).join(",");

function mimeDoArquivo(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return MIME_POR_EXTENSAO[ext] ?? "";
}

function lerComoDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export interface AnexosControle {
  anexos: AnexoNovo[];
  handlePaste: (e: ClipboardEvent) => void;
  handleFilePick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removerAnexo: (index: number) => void;
  limpar: () => void;
}

export function useAnexos(
  aoAdicionar?: (novos: AnexoNovo[]) => void,
): AnexosControle {
  const notify = useNotification();
  const aoAdicionarRef = useRef(aoAdicionar);
  aoAdicionarRef.current = aoAdicionar;

  const [anexos, setAnexos] = useState<AnexoNovo[]>([]);
  const anexosRef = useRef<AnexoNovo[]>([]);
  // Serializa os lotes: dois Ctrl+V seguidos partiriam da mesma lista antiga.
  const filaRef = useRef<Promise<void>>(Promise.resolve());

  const commit = useCallback((proximos: AnexoNovo[]) => {
    anexosRef.current = proximos;
    setAnexos(proximos);
  }, []);

  const adicionar = useCallback(
    async (files: File[]) => {
      const tiposAceitos = files.filter((file) => {
        const mime = mimeDoArquivo(file);
        return !!MIMES_PERMITIDOS[mime];
      });
      if (tiposAceitos.length < files.length) {
        notify("Tipo de arquivo não permitido.", "warning");
      }
      if (!tiposAceitos.length) return;

      const aceitos = tiposAceitos.filter(
        (file) => file.size <= MAX_ARQUIVO_BYTES,
      );
      if (aceitos.length < tiposAceitos.length) {
        notify(
          `Cada arquivo deve ter no máximo ${MAX_ARQUIVO_MB} MB.`,
          "warning",
        );
      }
      if (!aceitos.length) return;

      const lote = filaRef.current.then(async () => {
        const urls = await Promise.all(aceitos.map(lerComoDataUrl));
        const atuais = anexosRef.current;
        let bytes = atuais.reduce((soma, anexo) => soma + anexo.url.length, 0);
        const cabem: AnexoNovo[] = [];

        for (let i = 0; i < urls.length; i++) {
          if (bytes + urls[i].length > MAX_TOTAL_BYTES) break;
          bytes += urls[i].length;
          const mimeType = mimeDoArquivo(aceitos[i]);
          cabem.push({
            nome: aceitos[i].name,
            url: urls[i],
            mimeType,
            tamanho: aceitos[i].size,
            tipo: tipoDoMime(mimeType),
          });
        }

        if (cabem.length < urls.length) {
          notify("O total de anexos excede o limite. Envie em partes.", "warning");
        }
        if (cabem.length) {
          commit([...atuais, ...cabem]);
          aoAdicionarRef.current?.(cabem);
        }
      });

      filaRef.current = lote.catch(() => {
        notify("Não foi possível ler o arquivo.", "error");
      });
      await filaRef.current;
    },
    [commit, notify],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file" && items[i].type.startsWith("image/")) {
          const file = items[i].getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        void adicionar(files);
      }
    },
    [adicionar],
  );

  const handleFilePick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      void adicionar(files);
      e.target.value = "";
    },
    [adicionar],
  );

  const removerAnexo = useCallback(
    (index: number) => {
      commit(anexosRef.current.filter((_, i) => i !== index));
    },
    [commit],
  );

  const limpar = useCallback(() => commit([]), [commit]);

  return { anexos, handlePaste, handleFilePick, removerAnexo, limpar };
}

export interface AnexoExibivel {
  url: string;
  nome?: string;
  tamanho?: number;
  tipo: TipoAnexo;
}

function baixar(anexo: AnexoExibivel) {
  const link = document.createElement("a");
  link.href = anexo.url;
  link.download = anexo.nome || "anexo";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

interface AnexoThumbsProps {
  itens: AnexoExibivel[];
  onPreview: (url: string) => void;
  onRemove?: (index: number) => void;
}

export function AnexoThumbs({ itens, onPreview, onRemove }: AnexoThumbsProps) {
  const theme = useTheme();
  const styles = styless(theme);

  if (!itens.length) return null;

  return (
    <Box sx={styles.anexosLinha}>
      {itens.map((anexo, i) => {
        const remover = onRemove ? (
          <IconButton
            size="small"
            aria-label="Remover anexo"
            sx={anexo.tipo === "imagem" ? styles.miniaturaRemover : styles.arquivoRemover}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(i);
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        ) : null;

        if (anexo.tipo === "imagem") {
          return (
            <Box
              key={`${anexo.url}-${i}`}
              sx={styles.miniatura}
              onClick={() => onPreview(anexo.url)}
            >
              <img src={anexo.url} alt={anexo.nome || `anexo ${i + 1}`} />
              {remover}
            </Box>
          );
        }

        const tamanho = formatarTamanho(anexo.tamanho);
        return (
          <Box
            key={`${anexo.nome}-${i}`}
            sx={styles.arquivoChip}
            title={anexo.nome}
            onClick={() => baixar(anexo)}
          >
            <InsertDriveFileOutlinedIcon sx={styles.arquivoIcone} />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={styles.arquivoNome}>{anexo.nome || "anexo"}</Box>
              {!!tamanho && <Box sx={styles.arquivoTamanho}>{tamanho}</Box>}
            </Box>
            {remover}
          </Box>
        );
      })}
    </Box>
  );
}

const ZOOM_MAX = 8;
const ZOOM_PASSO = 1.2;
const ZOOM_DUPLO = 2.5;

interface LightboxProps {
  src: string | null;
  onClose: () => void;
}

export function Lightbox({ src, onClose }: LightboxProps) {
  const [escala, setEscala] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const arrasteRef = useRef<{ x: number; y: number } | null>(null);
  const palcoRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setEscala(1);
    setPos({ x: 0, y: 0 });
  }, [src]);

  const limitar = (p: { x: number; y: number }, nivel: number) => {
    const palco = palcoRef.current;
    const img = imgRef.current;
    if (!palco || !img) return p;
    const maxX = Math.max(0, (img.offsetWidth * nivel - palco.clientWidth) / 2);
    const maxY = Math.max(
      0,
      (img.offsetHeight * nivel - palco.clientHeight) / 2,
    );
    return {
      x: Math.min(maxX, Math.max(-maxX, p.x)),
      y: Math.min(maxY, Math.max(-maxY, p.y)),
    };
  };

  const reiniciar = () => {
    setEscala(1);
    setPos({ x: 0, y: 0 });
  };

  const zoomNoPonteiro = (nivel: number, clientX: number, clientY: number) => {
    const proxima = Math.min(ZOOM_MAX, Math.max(1, nivel));
    if (proxima === 1) return reiniciar();
    const palco = palcoRef.current;
    if (!palco) return setEscala(proxima);

    const area = palco.getBoundingClientRect();
    const alvoX = clientX - (area.left + area.width / 2);
    const alvoY = clientY - (area.top + area.height / 2);
    const razao = proxima / escala;

    setEscala(proxima);
    setPos(
      limitar(
        {
          x: alvoX - (alvoX - pos.x) * razao,
          y: alvoY - (alvoY - pos.y) * razao,
        },
        proxima,
      ),
    );
  };

  return (
    <Dialog
      open={!!src}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          m: 0,
          maxWidth: "none",
          maxHeight: "none",
          backgroundColor: "transparent",
          backgroundImage: "none",
          border: 0,
          boxShadow: "none",
          overflow: "hidden",
        },
      }}
    >
      {src && (
        <Box
          ref={palcoRef}
          sx={{
            display: "inline-flex",
            lineHeight: 0,
            overflow: "hidden",
            userSelect: "none",
            cursor: escala > 1 ? "grab" : "zoom-in",
            "&:active": escala > 1 ? { cursor: "grabbing" } : {},
          }}
          onWheel={(e) =>
            zoomNoPonteiro(
              escala * (e.deltaY < 0 ? ZOOM_PASSO : 1 / ZOOM_PASSO),
              e.clientX,
              e.clientY,
            )
          }
          onDoubleClick={(e) =>
            escala > 1
              ? reiniciar()
              : zoomNoPonteiro(ZOOM_DUPLO, e.clientX, e.clientY)
          }
          onContextMenu={(e) => {
            e.preventDefault();
            reiniciar();
          }}
          onMouseDown={(e) => {
            if (escala === 1) return;
            arrasteRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
          }}
          onMouseMove={(e) => {
            const origem = arrasteRef.current;
            if (!origem) return;
            setPos(
              limitar(
                { x: e.clientX - origem.x, y: e.clientY - origem.y },
                escala,
              ),
            );
          }}
          onMouseUp={() => {
            arrasteRef.current = null;
          }}
          onMouseLeave={() => {
            arrasteRef.current = null;
          }}
        >
          <Box
            component="img"
            ref={imgRef}
            src={src}
            alt="Pré-visualização"
            draggable={false}
            sx={{
              maxWidth: "96vw",
              maxHeight: "92vh",
              display: "block",
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${escala})`,
            }}
          />
        </Box>
      )}
    </Dialog>
  );
}
